import { scope } from '#components/generics/js/selectors/scope.js'
import * as generics from '#components/generics/js/styles.js'
import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'
import input from '@mailobj-browser/front/js/events/types/input.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import removed from '@mailobj-browser/front/js/tree/removed.js'
import replaceChildren from '@mailobj-browser/front/js/tree/replaceChildren.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import root from '@mailobj-browser/front/js/utils/root.js'
import currency from '#utilities/js/format/currency.js'
import date from '#utilities/js/format/date.js'
import * as utilities from '#utilities/js/styles.js'
import with_vat from '#utilities/js/vat/with_vat.js'
import without_vat from '#utilities/js/vat/without_vat.js'
import { modifiers } from '../../../styles.js'

const { parseFloat } = Number
const cartsParams = new WeakMap()

const onInput = object(listener, {
  type: input,
  passive,
  task: (
    select
  ) => {
    const { form, name, options } = select
    const [option] = options
    const cartParams = cartsParams.get(form)
    let changes = {}
    changes[name] = fields[name](option)

    if ('price' in changes[name]
      && (('price' in cartParams[name] && changes[name].price !== cartParams[name].price)
        || ('included' in cartParams[name] && !('price' in cartParams[name])))
      || 'value' in changes[name] && changes[name].value !== cartParams[name].value) {
      updateCart(form, name, changes)
    }
  }
})

const fields = object(null, {
  sub_npremium: (option) => {
    const { label, price } = scope(option)

    return {
      label,
      price: parseFloat(price)
    }
  },
  sub_storage: (option) => {
    const { label, price } = scope(option)

    return {
      label: label !== undefined ? label : null,
      price: parseFloat(price)
    }
  },
  sub_meet: (option) => {
    const { label, price } = scope(option)

    return {
      label: label !== undefined ? label : null,
      price: parseFloat(price)
    }
  },
  sub_websites: (option) => {
    const { label, price } = scope(option)

    return {
      label: label !== undefined ? label : null,
      price: parseFloat(price)
    }
  },
  sub_duration: (option) => {
    const { label, value } = scope(option)

    return {
      label,
      value: parseFloat(value)
    }
  }
})

const updateCart = (form, name, changes) => {
  const cart = one(`.${generics.elements.cart}`, form)
  let cartParams = cartsParams.get(form)
  const { mode } = cartParams
  const rootCartParams = root(cartParams)
  const button = one(`.${generics.modifiers.button.is_primary}`, cart)
  const notSubmittableInfoElement = one(`.${generics.elements.cart_not_submittable_info}`, cart)
  let monthlyPrice

  displayLines(form, { changes, name })

  cartsParams.set(form, object(cartParams, changes))
  cartParams = cartsParams.get(form)
  monthlyPrice = cartParams.monthly_price

  if (mode === 'modify_current' && !('is_modified' in rootCartParams)) {
    if (JSON.stringify(rootCartParams) === JSON.stringify(cartParams)) {
      button.disabled = true
      button.classList.add(utilities.modifiers.display.none)

      if (notSubmittableInfoElement) {
        notSubmittableInfoElement.classList.remove(utilities.modifiers.display.none)
      }
    } else if (monthlyPrice > 0) {
      button.disabled = false
      button.classList.remove(utilities.modifiers.display.none)
      if (notSubmittableInfoElement) {
        notSubmittableInfoElement.classList.add(utilities.modifiers.display.none)
      }
    }
  }

  modifyExpiry(form)
}

const modifyExpiry = (form) => {
  const format = scope(form)
  const { mode, expiry, sub_duration, monthly_price: monthlyPrice, is_with_vat: isWithVat } = cartsParams.get(form)
  const cartExpiryItemElement = one(`.${generics.elements.cart} .${generics.elements.cart_expiry_item}`, form)
  const cartExpiryElement = one(`.${generics.elements.cart_expiry}`, form)
  const now = Date.now() / 1000
  let currentExpiryDate = null
  let durationValue = null
  let elementOldExpiry = null
  let oldExpiry = null
  let expiryDate = null
  let timeLeft = 0
  let credit = 0
  let duration = 0
  let oldMonthly = null

  if (mode === 'extend' || mode === 'first') {
    durationValue = sub_duration.value
  }

  if (cartExpiryItemElement && monthlyPrice > 0) {
    cartExpiryItemElement.classList.remove(utilities.modifiers.display.none)
  }

  if (mode === 'extend') {
    if (cartExpiryElement) {
      currentExpiryDate = new Date(expiry * 1000)
      replaceChildren(cartExpiryElement,
        date(new Date(currentExpiryDate.setMonth(currentExpiryDate.getMonth() + durationValue)), format))
    }
  } else if (mode === 'first') {
    elementOldExpiry = one('#id_hidden_old_expiry', form)
    oldExpiry = Math.max(elementOldExpiry?.value ?? 0, now)
    expiryDate = new Date(oldExpiry * 1000)

    expiryDate.setMonth(expiryDate.getMonth() + durationValue)

    if (cartExpiryElement) {
      replaceChildren(cartExpiryElement, date(expiryDate, format))
    }
  } else {
    oldExpiry = one('#id_hidden_old_expiry', form).value
    timeLeft = oldExpiry > now ? oldExpiry - now : 0
    oldMonthly = one('#id_hidden_old_monthly_value', form).value

    if (!isWithVat) {
      oldMonthly = without_vat(oldMonthly, format)
    }

    credit = timeLeft * oldMonthly

    if (monthlyPrice > 0) {
      duration = Math.floor(credit / monthlyPrice)
      expiryDate = new Date((now + duration) * 1000)

      if (cartExpiryElement) {
        replaceChildren(cartExpiryElement, date(expiryDate, format))
      }
    }
  }
}

const cases = object(null, {
  sub_npremium: ({ form, format, label, price, remittance, cartItemOldPriceElement, priceComputed }) => {
    const subNPremiumLabelElement = one(`.${generics.elements.cart} .${modifiers.subscription_label.sub_npremium}`, form)
    const subNPremiumPriceElement = one(`.${generics.elements.cart} .${modifiers.subscription_price.sub_npremium}`, form)

    if (subNPremiumLabelElement) {
      displayPriceLine(subNPremiumLabelElement, label)
      if (remittance && remittance > 0 && cartItemOldPriceElement) {
        replaceChildren(cartItemOldPriceElement, currency(price, format))
      }
      if (subNPremiumPriceElement) {
        replaceChildren(subNPremiumPriceElement, currency(priceComputed, format))
      }
    }
  },
  sub_storage: ({ form, format, label, price }) => {
    const subStorageLabelElement = one(`.${generics.elements.cart} .${modifiers.subscription_label.sub_storage}`, form)
    const subStoragePriceElement = one(`.${generics.elements.cart} .${modifiers.subscription_price.sub_storage}`, form)

    if (subStorageLabelElement) {
      displayPriceLine(subStorageLabelElement, label)
      if (subStoragePriceElement) {
        replaceChildren(subStoragePriceElement, currency(price, format))
      }
    }
  },
  sub_meet: ({ form, format, label, price }) => {
    const subMeetLabelElement = one(`.${generics.elements.cart} .${modifiers.subscription_label.sub_meet}`, form)
    const subMeetPriceElement = one(`.${generics.elements.cart} .${modifiers.subscription_price.sub_meet}`, form)

    if (subMeetLabelElement) {
      displayPriceLine(subMeetLabelElement, label)
      if (subMeetPriceElement) {
        replaceChildren(subMeetPriceElement, currency(price, format))
      }
    }
  },
  sub_websites: ({ form, format, label, price }) => {
    const subWebsitesLabelElement = one(`.${generics.elements.cart} .${modifiers.subscription_label.sub_websites}`, form)
    const subWebsitesPriceElement = one(`.${generics.elements.cart} .${modifiers.subscription_price.sub_websites}`, form)

    if (subWebsitesLabelElement) {
      displayPriceLine(subWebsitesLabelElement, label)
      if (subWebsitesPriceElement) {
        replaceChildren(subWebsitesPriceElement, currency(price, format))
      }
    }
  },
  sub_duration: ({ form, label }) => {
    replaceChildren(one(`.${generics.elements.cart} .${generics.modifiers.cart_value.sub_duration}`, form), label)
  },
})

export const displayLines = (
  form,
  {
    changes,
    name
  }) => {
  const format = scope(form)
  const cartParams = cartsParams.get(form)
  const { monthly_price: monthlyPrice, remittance, is_with_vat: isWithVat, mode } = cartParams
  const cartExpiryItemElement = one(`.${generics.elements.cart} .${generics.elements.cart_expiry_item}`, form)
  const cartItemOldPriceElement = one(`.${generics.elements.cart} .${generics.elements.cart_item_old_price}`, form)
  let remittancePrice = 0
  let priceComputed
  let monthlyValue = monthlyPrice
  let price = changes[name].price
  let label = null

  if (changes[name].label) {
    label = changes[name].label
  }

  if (name !== 'sub_duration') {
    priceComputed = changes[name].price

    if (name === 'sub_npremium' && remittance && remittance > 0) {
      remittancePrice = changes[name].price * ((100 - remittance) / 100)
      priceComputed = remittancePrice
    }
    monthlyValue += remittancePrice > 0 ? remittancePrice : changes[name].price

    remittancePrice = 0
    if (changes[name].price !== cartParams[name].price && cartParams[name].price > 0) {
      if (name === 'sub_npremium' && remittance && remittance > 0) {
        remittancePrice = cartParams[name].price * ((100 - remittance) / 100)
      }

      monthlyValue -= remittancePrice > 0 ? remittancePrice : cartParams[name].price
    }
  }

  cases[name]({
    form,
    format,
    label,
    price,
    remittance,
    cartItemOldPriceElement,
    priceComputed,
    mode
  })

  changes.monthly_price = parseFloat(monthlyValue.toFixed(2))
  // changes = object(changes, { monthly_price: parseFloat(monthlyValue.toFixed(2)) })

  if (name !== 'sub_duration' && monthlyValue === 0 && cartExpiryItemElement) {
    cartExpiryItemElement.classList.add(utilities.modifiers.display.none)
  }

  calculateTotal(form, changes, price, isWithVat)

  if ('included' in cartParams[name]) {
    changes[name].included = cartParams[name].included
    // changes[name] = object(changes, { included: cartParams[name].included })
  }
}
const calculateTotal = (form, changes, price, isWithVat) => {
  const format = scope(form)
  const cartParams = cartsParams.get(form)
  const cartItemPriceMonthlyWithVatElement = one(`.${generics.elements.cart} .${generics.modifiers.cart_item_price.monthly_with_vat}`, form)
  const cartItemPriceTotalWithoutVatElement = one(`.${generics.elements.cart} .${generics.modifiers.cart_item_price.total_without_vat}`, form)
  const cartItemPriceTotalWithVatElement = one(`.${generics.elements.cart} .${generics.modifiers.cart_item_price.total_with_vat}`, form)
  const cartItemPriceMonthlyWithoutVatElement = one(`.${generics.elements.cart} .${generics.modifiers.cart_item_price.monthly_without_vat}`, form)
  const cartItemPriceTotalVatElement = one(`.${generics.elements.cart} .${generics.modifiers.cart_item_price.total_vat}`, form)
  let { monthly_price: monthlyPrice } = changes
  let annualValue = null
  let totalWithoutVAT

  if ('sub_duration' in changes) {
    annualValue = changes.sub_duration.value * monthlyPrice
  } else if ('sub_duration' in cartParams) {
    annualValue = cartParams.sub_duration.value * monthlyPrice
  }
  if (isWithVat) {
    if (cartItemPriceMonthlyWithVatElement) {
      replaceChildren(cartItemPriceMonthlyWithVatElement, currency(monthlyPrice, format))
    }

    if (annualValue) {
      totalWithoutVAT = without_vat(annualValue, format)
    }
  } else {
    if (cartItemPriceMonthlyWithoutVatElement) {
      replaceChildren(cartItemPriceMonthlyWithoutVatElement, currency(monthlyPrice, format))
    }

    if (annualValue) {
      totalWithoutVAT = annualValue
      annualValue = with_vat(annualValue, format)
    }
  }

  if (annualValue) {
    if (cartItemPriceTotalWithoutVatElement) {
      replaceChildren(cartItemPriceTotalWithoutVatElement, currency(totalWithoutVAT, format))
    }
    if (cartItemPriceTotalWithVatElement) {
      replaceChildren(cartItemPriceTotalWithVatElement, currency(annualValue, format))
    }

    if (cartItemPriceTotalVatElement) {
      replaceChildren(cartItemPriceTotalVatElement, currency(annualValue - totalWithoutVAT, format))
    }
  }
}

const displayPriceLine = (element, label) => {
  const cartItemElement = element.closest(`.${generics.elements.cart_item}`)
  if (label) {
    replaceChildren(element, label)
    cartItemElement.classList.remove(utilities.modifiers.display.none)
  } else {
    cartItemElement.classList.add(utilities.modifiers.display.none)
  }
}

export default form => {
  const cartElement = one(`.${generics.elements.cart}`, form)
  let cartParams = null

  if (cartElement) {
    cartParams = scope(cartElement)

    cartsParams.set(form, object(null, cartParams))

    for (const select of all('select', form)) {
      onInput.listen(select)
    }
  }

  queueMicrotask(async () => {
    await removed(form)
    if (cartsParams.get(form) === form) {
      cartsParams.delete(form)
    }
  })
}
